import type { Nullable } from "models-webgl2";
import { Vec2 } from "utils/math/vec2";
import { CommandAction } from "utils/helpers/classes/command-controller";
import { Webgl2dRenderer } from "../../../webgl2d-renderer";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { ImageRectComponent } from "../image-rect-component";
import { ImageAdjustRectComponent } from "../image-adjust-rect-component";
import { SelectionComponent } from "./selection-component";
import { Mat3 } from "utils/math/mat3";
import { hexToRgba } from "utils/styles/hex-to-rgba";

export class SelectionTopRightControlComponent extends Webgl2Component {
  private _size = 8;
  private _bgColor = "#ffffff";
  private _tm3: Mat3;
  private _tm3_2: Mat3;
  private _prevTransform: Webgl2Component["transform"];

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  constructor(
    private readonly _renderer: Webgl2dRenderer,
    private readonly _selectionComponent: SelectionComponent,
  ) {
    super();
    this.transform.setSize(this._size, this._size);
  }

  public onMouseDown() {
    if (!this._selectedComponent) return false;
    this._renderer.canvas.style.cursor = "nesw-resize";
    this._bgColor = "#0099A1";

    const bottomLeft = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._selectedComponent,
      Vec2.zeroOne(),
    );
    const center = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._selectedComponent,
      Vec2.half(),
    );
    this._tm3 = Mat3.identity()
      .flipY()
      .rotate(-this._selectedComponent.transform.rads)
      .translate(bottomLeft.clone().negate());
    this._tm3_2 = Mat3.identity()
      .flipY()
      .rotate(-this._selectedComponent.transform.rads)
      .translate(center.clone().negate());
    this._prevTransform = this._selectedComponent.transform.clone();
  }

  public onMouseMove(e: MouseEvent | TouchEvent) {
    if (!this._selectedComponent) return;

    const altKey = e.altKey || e.metaKey;
    const tm3 = altKey ? this._tm3_2 : this._tm3;
    const length = altKey
      ? this._prevTransform.size.length() / 2
      : this._prevTransform.size.length();
    const currentMousePos = this._renderer.camera
      .getGlobalMousePixelPosition(e)
      .transformMat3(tm3.mat);
    const size = this._prevTransform.size.clone().multiply(currentMousePos.length() / length);
    const transform = {
      size,
      position: (altKey ? Vec2.zero() : size.clone().divide(2)).transformMat3(
        tm3.clone().invert().mat,
      ),
    };
    this._selectedComponent.transform.calcDimensions(transform);
    this._selectionComponent.transform.calcDimensions(transform);
  }

  public onMouseUp() {
    this._renderer.canvas.style.cursor = "default";
    this._bgColor = "#ffffff";
    this._renderer.render();
    if (!this._selectedComponent) return;

    const undoTransform = this._prevTransform;
    this._renderer.commands.execute(
      new CommandAction(
        "transform",
        () => {
          /* intentionally empty */
        },
        () => {
          if (this._selectedComponent) {
            this._selectedComponent.transform.copy(undoTransform);
            this._selectionComponent.transform.copy(undoTransform);
          }
        },
      ),
    );
  }

  public getRenderData() {
    const material = Webgl2.materials.selectionControl;
    material.setUniforms({
      u_size: this.transform.size.getAbs().getArray(),
      u_bgColor: hexToRgba(this._bgColor, true),
    });
    return { material };
  }
}

export default SelectionTopRightControlComponent;
